import React, { useState, useMemo } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaFilePdf,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaFilter,
  FaEyeSlash,
  FaInfoCircle,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TrialBalanceComparisonTable = ({ reportData, viewMode = "net" }) => {
  const {
    years = [],
    accounts = [],
    summary = {},
    mode = "cumulative",
    latest_year: latestYear,
    prior_year: priorYear,
  } = reportData || {};

  // Hierarchy collapsed state
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Filtering states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("ALL");
  const [hideZeroBalances, setHideZeroBalances] = useState(false);

  // Expand / collapse all groups
  const isAnyCollapsed = Object.values(collapsedGroups).some((v) => v === true);

  const handleToggleAll = () => {
    if (isAnyCollapsed) {
      setCollapsedGroups({});
    } else {
      const collapsed = {};
      accounts.forEach((acc) => {
        if (acc.is_group) {
          collapsed[acc.id] = true;
        }
      });
      setCollapsedGroups(collapsed);
    }
  };

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Filter accounts based on search, type, and zero balances
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      // Type filter
      if (selectedType !== "ALL" && acc.type !== selectedType) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = acc.name?.toLowerCase().includes(query);
        const matchesCode = acc.code?.toLowerCase().includes(query);
        if (!matchesName && !matchesCode) {
          return false;
        }
      }

      // Hide zero balances (only keep leaf accounts with at least one non-zero balance, or keep groups)
      if (hideZeroBalances && !acc.is_group) {
        const hasActivity = years.some((y) => {
          const yrData = acc.yearly?.[y];
          return yrData && (Math.abs(yrData.debit) > 0 || Math.abs(yrData.credit) > 0 || Math.abs(yrData.net_balance) > 0);
        });
        if (!hasActivity) return false;
      }

      return true;
    });
  }, [accounts, selectedType, searchQuery, hideZeroBalances, years]);

  // Handle collapsible parent/child row visibility
  const visibleAccounts = useMemo(() => {
    const res = [];
    let collapseLevel = 999;

    for (const acc of filteredAccounts) {
      if (acc.level > collapseLevel) {
        continue;
      }
      collapseLevel = 999;

      if (collapsedGroups[acc.id]) {
        collapseLevel = acc.level;
      }
      res.push(acc);
    }
    return res;
  }, [filteredAccounts, collapsedGroups]);

  // ── Excel Export ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    const rows = [];

    // Header info
    rows.push(["Rekonic - Trial Balance Multi-Year Comparison Report"]);
    rows.push([`Mode: ${mode === "annual" ? "Annual Movement" : "Cumulative As of Year-End"}`]);
    rows.push([`Horizon: ${years.join(" vs ")}`]);
    rows.push([]);

    // Column headers
    if (viewMode === "dual") {
      const headerRow1 = ["Code", "Account Name", "Type"];
      years.forEach((yr) => {
        headerRow1.push(`${yr} (Dr)`, `${yr} (Cr)`);
      });
      rows.push(headerRow1);

      accounts.forEach((acc) => {
        const indent = "  ".repeat(acc.level);
        const row = [acc.code || "", `${indent}${acc.name}`, acc.type || ""];
        years.forEach((yr) => {
          const yrData = acc.yearly?.[yr] || { debit: 0, credit: 0 };
          row.push(yrData.debit, yrData.credit);
        });
        rows.push(row);
      });

      // Totals
      const totalDrCrRow = ["", "TOTALS", ""];
      years.forEach((yr) => {
        const sumYr = summary[yr] || {};
        totalDrCrRow.push(sumYr.total_debit || 0, sumYr.total_credit || 0);
      });
      rows.push([]);
      rows.push(totalDrCrRow);
    } else {
      // Net balance view
      const headerRow = ["Code", "Account Name", "Type", "Side"];
      years.forEach((yr) => {
        headerRow.push(`${yr} Balance`);
      });
      if (priorYear && latestYear) {
        headerRow.push("YoY Variance ($)", "YoY Change (%)");
      }
      rows.push(headerRow);

      accounts.forEach((acc) => {
        const indent = "  ".repeat(acc.level);
        const row = [acc.code || "", `${indent}${acc.name}`, acc.type || "", acc.yearly?.[latestYear]?.balance_side || "Dr"];
        years.forEach((yr) => {
          const yrData = acc.yearly?.[yr] || { net_balance: 0 };
          row.push(yrData.net_balance);
        });
        if (priorYear && latestYear) {
          row.push(acc.variance || 0, `${acc.variance_pct || 0}%`);
        }
        rows.push(row);
      });

      // Totals Row
      const totalDebitRow = ["", "TOTAL DEBITS (Dr)", "", ""];
      years.forEach((yr) => {
        totalDebitRow.push(summary[yr]?.total_debit || 0);
      });
      rows.push([]);
      rows.push(totalDebitRow);

      const totalCreditRow = ["", "TOTAL CREDITS (Cr)", "", ""];
      years.forEach((yr) => {
        totalCreditRow.push(summary[yr]?.total_credit || 0);
      });
      rows.push(totalCreditRow);

      const diffRow = ["", "DIFFERENCE", "", ""];
      years.forEach((yr) => {
        diffRow.push(summary[yr]?.difference || 0);
      });
      rows.push(diffRow);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Trial Balance Comparison");
    XLSX.writeFile(
      workbook,
      `Trial_Balance_${years.length}_Years_Comparison_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // ── PDF Export ────────────────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.setFontSize(16);
    doc.setTextColor(26, 43, 74);
    doc.text("Trial Balance Comparison Report", 40, 40);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(
      `Mode: ${mode === "annual" ? "Annual Movement" : "Cumulative As of Year-End"} | Years: ${years.join(" - ")}`,
      40,
      56
    );

    let head = [];
    let body = [];

    if (viewMode === "dual") {
      head = [["Code", "Account Name", ...years.flatMap((y) => [`${y} Dr`, `${y} Cr`])]];
      body = visibleAccounts.map((acc) => {
        const indent = "  ".repeat(acc.level);
        return [
          acc.code || "",
          `${indent}${acc.name}`,
          ...years.flatMap((y) => [
            fmt(acc.yearly?.[y]?.debit),
            fmt(acc.yearly?.[y]?.credit),
          ]),
        ];
      });

      // Total row
      body.push([
        "",
        "TOTALS",
        ...years.flatMap((y) => [
          fmt(summary[y]?.total_debit),
          fmt(summary[y]?.total_credit),
        ]),
      ]);
    } else {
      head = [
        [
          "Code",
          "Account Name",
          "Type",
          "Side",
          ...years.map((y) => `${y} Balance`),
          ...(priorYear ? ["YoY Variance", "YoY %"] : []),
        ],
      ];
      body = visibleAccounts.map((acc) => {
        const indent = "  ".repeat(acc.level);
        const latestSide = acc.yearly?.[latestYear]?.balance_side || "Dr";
        return [
          acc.code || "",
          `${indent}${acc.name}`,
          acc.type || "",
          latestSide,
          ...years.map((y) => fmt(Math.abs(acc.yearly?.[y]?.net_balance || 0))),
          ...(priorYear
            ? [`$${fmt(acc.variance)}`, `${acc.variance_pct > 0 ? "+" : ""}${acc.variance_pct}%`]
            : []),
        ];
      });

      // Totals
      body.push([
        "",
        "TOTAL DEBITS (Dr)",
        "",
        "",
        ...years.map((y) => fmt(summary[y]?.total_debit)),
        ...(priorYear ? ["", ""] : []),
      ]);
      body.push([
        "",
        "TOTAL CREDITS (Cr)",
        "",
        "",
        ...years.map((y) => fmt(summary[y]?.total_credit)),
        ...(priorYear ? ["", ""] : []),
      ]);
    }

    autoTable(doc, {
      head,
      body,
      startY: 70,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [0, 119, 204], textColor: 255 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 30, right: 30 },
    });

    doc.save(`Trial_Balance_Comparison_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      {/* ── Table Toolbar ──────────────────────────────────────────────────── */}
      <div className="card-header bg-white border-bottom py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          {/* Search & Filters */}
          <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1" style={{ maxWidth: "700px" }}>
            <div className="input-group input-group-sm" style={{ width: "220px" }}>
              <span className="input-group-text bg-light border-end-0">
                <FaSearch className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search account name / code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center gap-1">
              <FaFilter className="text-secondary small" />
              <select
                className="form-select form-select-sm"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                style={{ width: "135px" }}
              >
                <option value="ALL">All Types</option>
                <option value="Asset">Assets</option>
                <option value="Liability">Liabilities</option>
                <option value="Equity">Equity</option>
                <option value="Revenue">Revenue</option>
                <option value="Expense">Expense</option>
              </select>
            </div>

            <button
              type="button"
              className={`btn btn-sm ${
                hideZeroBalances ? "btn-secondary text-white" : "btn-outline-secondary"
              }`}
              onClick={() => setHideZeroBalances(!hideZeroBalances)}
              title="Toggle hiding accounts with zero balances across all years"
            >
              <FaEyeSlash className="me-1" />
              {hideZeroBalances ? "Active Only" : "Show All"}
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={handleToggleAll}
            >
              {isAnyCollapsed ? "↔ Expand All" : "↕ Collapse All"}
            </button>
          </div>

          {/* Export Actions */}
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 shadow-sm"
              onClick={exportExcel}
              title="Download Excel Spreadsheet"
            >
              <FaFileExcel /> Excel
            </button>
            <button
              className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 shadow-sm"
              onClick={exportPDF}
              title="Export PDF Document"
            >
              <FaFilePdf /> PDF
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Container ────────────────────────────────────────────────── */}
      <div className="table-responsive" style={{ maxHeight: "72vh" }}>
        <table className="table table-hover align-middle mb-0" style={{ fontSize: "0.875rem" }}>
          <thead className="table-light sticky-top" style={{ zIndex: 5 }}>
            {viewMode === "dual" ? (
              <>
                <tr>
                  <th style={{ width: "90px" }} rowSpan={2}>Code</th>
                  <th style={{ minWidth: "260px" }} rowSpan={2}>Account Name</th>
                  <th style={{ width: "100px" }} rowSpan={2}>Type</th>
                  {years.map((yr) => (
                    <th
                      key={yr}
                      colSpan={2}
                      className="text-center border-start"
                      style={{
                        background: yr === latestYear ? "#e0f2fe" : undefined,
                        color: yr === latestYear ? "#0369a1" : undefined,
                        fontWeight: yr === latestYear ? 700 : 600,
                      }}
                    >
                      FY {yr} {yr === latestYear && <span className="badge bg-primary ms-1">Current</span>}
                    </th>
                  ))}
                </tr>
                <tr>
                  {years.map((yr) => (
                    <React.Fragment key={`sub-${yr}`}>
                      <th className="text-end border-start small text-success py-1">Debit (Dr)</th>
                      <th className="text-end small text-danger py-1">Credit (Cr)</th>
                    </React.Fragment>
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <th style={{ width: "90px" }}>Code</th>
                <th style={{ minWidth: "260px" }}>Account Name</th>
                <th style={{ width: "95px" }}>Type</th>
                <th style={{ width: "65px" }} className="text-center">Side</th>
                {years.map((yr) => (
                  <th
                    key={yr}
                    className="text-end"
                    style={{
                      minWidth: "120px",
                      background: yr === latestYear ? "#e0f2fe" : undefined,
                      color: yr === latestYear ? "#0369a1" : undefined,
                      fontWeight: yr === latestYear ? 700 : 600,
                    }}
                  >
                    FY {yr}
                    {yr === latestYear && (
                      <span className="badge bg-primary ms-1" style={{ fontSize: "0.65rem" }}>
                        Current
                      </span>
                    )}
                  </th>
                ))}
                {priorYear && (
                  <>
                    <th className="text-end" style={{ minWidth: "115px", color: "#475569" }}>
                      YoY Variance
                    </th>
                    <th className="text-end" style={{ minWidth: "90px", color: "#475569" }}>
                      YoY %
                    </th>
                  </>
                )}
              </tr>
            )}
          </thead>

          <tbody>
            {visibleAccounts.length === 0 ? (
              <tr>
                <td colSpan={viewMode === "dual" ? 3 + years.length * 2 : 4 + years.length + (priorYear ? 2 : 0)} className="text-center py-5 text-muted">
                  No accounts found matching your filter criteria.
                </td>
              </tr>
            ) : (
              visibleAccounts.map((acc, idx) => {
                const isGroup = acc.is_group;
                const isCollapsed = !!collapsedGroups[acc.id];
                const latestData = acc.yearly?.[latestYear] || {};
                const isDebit = latestData.balance_side === "Dr";

                return (
                  <tr
                    key={acc.id}
                    style={{
                      background: isGroup ? (acc.level === 0 ? "#f1f5f9" : "#f8fafc") : undefined,
                      fontWeight: isGroup ? 600 : 400,
                      cursor: isGroup ? "pointer" : "default",
                    }}
                    onClick={() => isGroup && toggleGroup(acc.id)}
                  >
                    {/* Account Code */}
                    <td className="text-muted font-monospace" style={{ fontSize: "0.8rem" }}>
                      {acc.code || "—"}
                    </td>

                    {/* Account Name with Indentation & Hierarchy Carets */}
                    <td
                      style={{
                        paddingLeft: `${acc.level * 22 + 12}px`,
                        userSelect: "none",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        {isGroup ? (
                          <span className="text-primary me-1" style={{ fontSize: "0.85rem" }}>
                            {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                          </span>
                        ) : (
                          <span style={{ width: "16px", display: "inline-block" }}>•</span>
                        )}
                        <span className={isGroup ? (acc.level === 0 ? "fw-bold text-dark" : "fw-semibold text-secondary") : "text-dark"}>
                          {acc.name}
                        </span>
                      </div>
                    </td>

                    {/* Type Badge */}
                    <td>
                      <span
                        className="badge"
                        style={{
                          fontSize: "0.72rem",
                          backgroundColor:
                            acc.type === "Asset"
                              ? "#e0f2fe"
                              : acc.type === "Liability"
                              ? "#fef3c7"
                              : acc.type === "Equity"
                              ? "#ede9fe"
                              : acc.type === "Revenue"
                              ? "#dcfce7"
                              : "#fee2e2",
                          color:
                            acc.type === "Asset"
                              ? "#0369a1"
                              : acc.type === "Liability"
                              ? "#b45309"
                              : acc.type === "Equity"
                              ? "#6d28d9"
                              : acc.type === "Revenue"
                              ? "#15803d"
                              : "#b91c1c",
                        }}
                      >
                        {acc.type || "—"}
                      </span>
                    </td>

                    {/* DUAL MODE */}
                    {viewMode === "dual" ? (
                      years.map((yr) => {
                        const yrData = acc.yearly?.[yr] || { debit: 0, credit: 0 };
                        return (
                          <React.Fragment key={`${acc.id}-${yr}`}>
                            <td
                              className="text-end border-start"
                              style={{
                                color: yrData.debit > 0 ? "#15803d" : "#94a3b8",
                                background: yr === latestYear ? "rgba(224, 242, 254, 0.25)" : undefined,
                              }}
                            >
                              {yrData.debit > 0 ? fmt(yrData.debit) : "—"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                color: yrData.credit > 0 ? "#b91c1c" : "#94a3b8",
                                background: yr === latestYear ? "rgba(224, 242, 254, 0.25)" : undefined,
                              }}
                            >
                              {yrData.credit > 0 ? fmt(yrData.credit) : "—"}
                            </td>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      /* NET BALANCE MODE */
                      <>
                        <td className="text-center">
                          <span
                            className={`badge ${
                              isDebit ? "bg-success-subtle text-success border border-success-subtle" : "bg-danger-subtle text-danger border border-danger-subtle"
                            }`}
                            style={{ fontSize: "0.7rem", padding: "3px 6px" }}
                          >
                            {latestData.balance_side || "Dr"}
                          </span>
                        </td>

                        {years.map((yr) => {
                          const yrData = acc.yearly?.[yr] || { net_balance: 0 };
                          const netVal = yrData.net_balance || 0;
                          return (
                            <td
                              key={`${acc.id}-${yr}`}
                              className="text-end"
                              style={{
                                fontWeight: isGroup ? 600 : 400,
                                color: netVal === 0 ? "#94a3b8" : isDebit ? "#0f766e" : "#b91c1c",
                                background: yr === latestYear ? "rgba(224, 242, 254, 0.25)" : undefined,
                              }}
                            >
                              {netVal !== 0 ? fmt(Math.abs(netVal)) : "—"}
                            </td>
                          );
                        })}

                        {priorYear && (
                          <>
                            <td className="text-end">
                              <span
                                style={{
                                  fontWeight: 500,
                                  color: acc.variance > 0 ? "#16a34a" : acc.variance < 0 ? "#dc2626" : "#64748b",
                                }}
                              >
                                {acc.variance > 0 ? "+" : ""}
                                {fmt(acc.variance)}
                              </span>
                            </td>
                            <td className="text-end">
                              <span
                                className={`badge ${
                                  acc.variance > 0
                                    ? "bg-success-subtle text-success"
                                    : acc.variance < 0
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-light text-secondary"
                                }`}
                                style={{ fontSize: "0.75rem" }}
                              >
                                {acc.variance > 0 ? <FaArrowUp className="me-1" /> : acc.variance < 0 ? <FaArrowDown className="me-1" /> : null}
                                {acc.variance_pct > 0 ? "+" : ""}
                                {acc.variance_pct}%
                              </span>
                            </td>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>

          {/* ── Table Footer Totals ────────────────────────────────────────── */}
          <tfoot className="table-light border-top border-2 border-dark-subtle fw-bold">
            {viewMode === "dual" ? (
              <>
                <tr className="bg-light">
                  <td colSpan={3}>TOTAL DEBITS &amp; CREDITS</td>
                  {years.map((yr) => {
                    const yrSum = summary[yr] || {};
                    return (
                      <React.Fragment key={`tot-${yr}`}>
                        <td className="text-end border-start text-success">
                          ${fmt(yrSum.total_debit)}
                        </td>
                        <td className="text-end text-danger">
                          ${fmt(yrSum.total_credit)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
                <tr style={{ background: "#f8fafc" }}>
                  <td colSpan={3}>BALANCE INTEGRITY</td>
                  {years.map((yr) => {
                    const yrSum = summary[yr] || {};
                    const isBal = yrSum.is_balanced;
                    return (
                      <td key={`stat-${yr}`} colSpan={2} className="text-center border-start py-2">
                        {isBal ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                            <FaCheckCircle className="me-1" /> Balanced ($0.00)
                          </span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">
                            <FaTimesCircle className="me-1" /> Diff: ${fmt(Math.abs(yrSum.difference || 0))}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </>
            ) : (
              <>
                <tr style={{ background: "#f8fafc" }}>
                  <td colSpan={4}>TOTAL DEBITS (Dr)</td>
                  {years.map((yr) => (
                    <td key={`dr-${yr}`} className="text-end text-success">
                      ${fmt(summary[yr]?.total_debit)}
                    </td>
                  ))}
                  {priorYear && <td colSpan={2}></td>}
                </tr>

                <tr style={{ background: "#f8fafc" }}>
                  <td colSpan={4}>TOTAL CREDITS (Cr)</td>
                  {years.map((yr) => (
                    <td key={`cr-${yr}`} className="text-end text-danger">
                      ${fmt(summary[yr]?.total_credit)}
                    </td>
                  ))}
                  {priorYear && <td colSpan={2}></td>}
                </tr>

                <tr className="bg-light">
                  <td colSpan={4}>BALANCE VERIFICATION</td>
                  {years.map((yr) => {
                    const isBal = summary[yr]?.is_balanced;
                    return (
                      <td key={`ver-${yr}`} className="text-end">
                        {isBal ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle">
                            <FaCheckCircle className="me-1" /> Balanced
                          </span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                            <FaTimesCircle className="me-1" /> Off: ${fmt(Math.abs(summary[yr]?.difference || 0))}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  {priorYear && <td colSpan={2}></td>}
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      <div className="card-footer bg-white text-muted small py-2 d-flex align-items-center gap-2">
        <FaInfoCircle className="text-primary" />
        <span>
          Root accounts roll up sub-ledger accounts hierarchically. Balance integrity verifies Dr = Cr for all fiscal years.
        </span>
      </div>
    </div>
  );
};

export default TrialBalanceComparisonTable;
